import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Container,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Alert,
  Snackbar,
  Paper,
  Divider,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  SelectChangeEvent,
} from '@mui/material';
import {
  ArrowBack,
  Edit,
  Save,
  Cancel,
  Delete,
  // Person,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { bookingsService } from './bookings.service';
import { servicesService } from '../services/services.service';
import { Service } from '../services/services.dto';
import { diagnosticCentersService } from './diagnostic-centers.service';
import { DiagnosticCenter } from './diagnostic-centers.dto';
// import { Booking } from './bookings.dto';
import {
  BOOKING_STATUSES,
  BOOKING_STATUS,
  DEFAULT_CURRENCY,
} from '@/constants';
import { UpdateBookingDto } from './bookings.dto';
import { formatDate, formatTime } from '../../helpers';

const EditBooking = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { token, logout } = useAuth();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    healthCard: '',
    versionCode: '',
    dob: '',
    gender: 'male',
    streetAddress: '',
    city: '',
    postalCode: '',
    email: '',
    phone: '',
    homeNumber: '',
    workNumber: '',
    diagnosticCenterId: '',
    diagnosticCenterPhone: '',
    diagnosticCenterEmail: '',
    diagnosticCenterAddress: '',
    diagnosticCenterCity: '',
    diagnosticCenterPostalCode: '',
    status: 0,
    reportStatus: 0 as number | null,
    bookingDate: '',
    bookingTime: '',
    medicalInformation: '',
    serviceId: 0,
    amount: 0,
    bookingRef: '',
    createdAt: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [services, setServices] = useState<Service[]>([]);
  const [diagnosticCenters, setDiagnosticCenters] = useState<
    DiagnosticCenter[]
  >([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'warning' | 'info',
  });

  const genderOptions = [
    { value: 'male', label: 'Male' },
    { value: 'female', label: 'Female' },
    { value: 'other', label: 'Other' },
  ];

  // Set up logout callback and fetch data
  useEffect(() => {
    // Set up logout callbacks
    bookingsService.setLogoutCallback(logout);
    servicesService.setLogoutCallback(logout);
    diagnosticCentersService.setLogoutCallback(logout);

    const fetchData = async () => {
      if (!token || !id) return;

      try {
        setLoading(true);

        // Fetch booking data, services, and diagnostic centers in parallel
        const [booking, servicesData, centersData] = await Promise.all([
          bookingsService.getBooking(token, parseInt(id)),
          servicesService.getServices(token, { limit: 100, status: 1 }),
          diagnosticCentersService.getDiagnosticCenters(token, {
            limit: 100,
            status: 1,
          }),
        ]);

        console.log('Fetched booking data:', booking);
        console.log('Fetched services:', servicesData.data);
        console.log('Fetched diagnostic centers:', centersData.data);

        setFormData({
          firstName: booking.firstName,
          lastName: booking.lastName,
          healthCard: booking.healthCard || '',
          versionCode: booking.versionCode || '',
          dob: booking.dob,
          gender: booking.gender,
          streetAddress: booking.streetAddress || '',
          city: booking.city || '',
          postalCode: booking.postalCode || '',
          email: booking.email,
          phone: booking.phone,
          homeNumber: booking.homeNumber || '',
          workNumber: booking.workNumber || '',
          diagnosticCenterId: booking.diagnosticCenterId || '',
          diagnosticCenterPhone: booking.diagnosticCenterPhone || '',
          diagnosticCenterEmail: booking.diagnosticCenterEmail || '',
          diagnosticCenterAddress: booking.diagnosticCenterAddress || '',
          diagnosticCenterCity: booking.diagnosticCenterCity || '',
          diagnosticCenterPostalCode: booking.diagnosticCenterPostalCode || '',
          status: booking.status,
          reportStatus: booking.reportStatus || 0,
          bookingDate: booking.bookingDate,
          bookingTime: booking.bookingTime,
          medicalInformation: booking.medicalInformation || '',
          serviceId: booking.serviceId,
          amount: Number(booking.amount),
          bookingRef: booking.bookingRef,
          createdAt: booking.createdAt,
        });

        setServices(servicesData.data);
        setDiagnosticCenters(centersData.data);
      } catch (err) {
        if (err instanceof Error) {
          setSnackbar({
            open: true,
            message: err.message,
            severity: 'error',
          });
        } else {
          setSnackbar({
            open: true,
            message: 'Failed to fetch booking data',
            severity: 'error',
          });
        }
        navigate('/bookings');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token, id, navigate, logout]);

  const handleChange =
    (field: keyof typeof formData) =>
    (
      event:
        | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
        | SelectChangeEvent
    ) => {
      let rawValue = (event.target as HTMLInputElement).value;

      // Special handling for phone fields - only allow + and numbers
      if (
        field === 'phone' ||
        field === 'homeNumber' ||
        field === 'workNumber'
      ) {
        rawValue = rawValue.replace(/[^+0-9]/g, '');
        // Ensure + can only be at the start
        if (rawValue.includes('+')) {
          const plusCount = (rawValue.match(/\+/g) || []).length;
          if (plusCount > 1 || rawValue.indexOf('+') !== 0) {
            rawValue = rawValue.replace(/\+/g, '');
            if ((event.target as HTMLInputElement).value.startsWith('+')) {
              rawValue = '+' + rawValue;
            }
          }
        }
      }

      // If service is selected, auto-fill the amount
      if (field === 'serviceId') {
        const selectedService = services.find(s => s.id === Number(rawValue));
        if (selectedService) {
          setFormData({
            ...formData,
            serviceId: Number(rawValue),
            amount: Number(selectedService.price || 0),
          });
        } else {
          setFormData({ ...formData, [field]: Number(rawValue) });
        }
      } else if (field === 'diagnosticCenterId') {
        // If diagnostic center is selected, auto-fill center details
        const selectedCenter = diagnosticCenters.find(
          c => c.id === Number(rawValue)
        );
        if (selectedCenter) {
          setFormData({
            ...formData,
            diagnosticCenterId: String(selectedCenter.id),
            diagnosticCenterAddress: selectedCenter.address || '',
            diagnosticCenterCity: selectedCenter.city || '',
            diagnosticCenterPostalCode: selectedCenter.postalCode || '',
            diagnosticCenterEmail: selectedCenter.email || '',
            diagnosticCenterPhone: selectedCenter.phone || '',
          });
        } else {
          setFormData({ ...formData, [field]: rawValue });
        }
      } else if (field === 'status') {
        setFormData({ ...formData, [field]: Number(rawValue) });
      } else {
        setFormData({ ...formData, [field]: rawValue as any });
      }

      if (errors[field as string]) {
        setErrors({ ...errors, [field as string]: '' });
      }
    };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\+?\d+$/.test(formData.phone)) {
      newErrors.phone = 'Phone number can only contain + and numbers';
    }

    if (!formData.dob) {
      newErrors.dob = 'Date of birth is required';
    }

    if (!formData.bookingDate) {
      newErrors.bookingDate = 'Booking date is required';
    }

    if (!formData.bookingTime) {
      newErrors.bookingTime = 'Booking time is required';
    }

    if (!formData.serviceId || formData.serviceId === 0) {
      newErrors.serviceId = 'Service is required';
    }

    if (
      !formData.amount ||
      parseFloat(formData.amount.toString() as unknown as string) < 0
    ) {
      newErrors.amount = 'Valid amount is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm() || !token || !id) return;

    setSaving(true);
    try {
      const bookingData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        healthCard: formData.healthCard || undefined,
        versionCode: formData.versionCode || undefined,
        dob: formData.dob,
        gender: formData.gender,
        streetAddress: formData.streetAddress || undefined,
        city: formData.city || undefined,
        postalCode: formData.postalCode || undefined,
        email: formData.email,
        phone: formData.phone,
        homeNumber: formData.homeNumber || undefined,
        workNumber: formData.workNumber || undefined,
        diagnosticCenterId: formData.diagnosticCenterId || undefined,
        diagnosticCenterPhone: formData.diagnosticCenterPhone || undefined,
        diagnosticCenterEmail: formData.diagnosticCenterEmail || undefined,
        diagnosticCenterAddress: formData.diagnosticCenterAddress || undefined,
        diagnosticCenterCity: formData.diagnosticCenterCity || undefined,
        diagnosticCenterPostalCode:
          formData.diagnosticCenterPostalCode || undefined,
        status: formData.status,
        bookingDate: formData.bookingDate,
        bookingTime: formData.bookingTime,
        medicalInformation: formData.medicalInformation || undefined,
        serviceId: formData.serviceId,
        amount: Number(formData.amount),
      };

      await bookingsService.updateBooking(
        token,
        parseInt(id),
        bookingData as unknown as UpdateBookingDto
      );

      setSnackbar({
        open: true,
        message: 'Booking updated successfully!',
        severity: 'success',
      });

      setTimeout(() => {
        navigate('/bookings');
      }, 1500);
    } catch (err) {
      setSnackbar({
        open: true,
        message:
          err instanceof Error ? err.message : 'Failed to update booking',
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/bookings');
  };

  const handleDelete = async () => {
    if (!token || !id) return;

    try {
      await bookingsService.deleteBooking(token, parseInt(id));
      setSnackbar({
        open: true,
        message: 'Booking deleted successfully!',
        severity: 'success',
      });

      setTimeout(() => {
        navigate('/bookings');
      }, 1500);
    } catch (err) {
      if (err instanceof Error) {
        setSnackbar({
          open: true,
          message: err.message,
          severity: 'error',
        });
      } else {
        setSnackbar({
          open: true,
          message: 'Failed to delete booking',
          severity: 'error',
        });
      }
    }
    setDeleteDialog(false);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const getStatus = (statusKey: number) => {
    const statusResult = BOOKING_STATUS(statusKey);
    return statusResult;
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <CircularProgress />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Loading booking data...
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        {/* Header */}
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={2}
          >
            <Box display="flex" alignItems="center">
              <IconButton onClick={() => navigate('/bookings')} sx={{ mr: 2 }}>
                <ArrowBack />
              </IconButton>
              <Edit color="primary" sx={{ mr: 1 }} />
              <Typography variant="h4" component="h1">
                Edit Booking
              </Typography>
            </Box>
            <Box display="flex" gap={1} alignItems="center">
              <Typography variant="body2" color="text.secondary">
                {formData.bookingRef}
              </Typography>
              <Chip
                label={getStatus(formData.status).label}
                color={getStatus(formData.status).color as any}
                variant="outlined"
              />
            </Box>
          </Box>
          <Typography variant="body1" color="text.secondary">
            Update booking information and appointment details
          </Typography>
        </Paper>

        <Grid container spacing={3}>
          {/* Booking Settings Section */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Booking Settings
                </Typography>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={String(formData.status)}
                    label="Status"
                    onChange={handleChange('status')}
                  >
                    {BOOKING_STATUSES.map(status => (
                      <MenuItem key={status.key} value={String(status.key)}>
                        {status.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl
                  fullWidth
                  margin="normal"
                  error={!!errors.serviceId}
                >
                  <InputLabel>Service *</InputLabel>
                  <Select
                    value={String(formData.serviceId)}
                    label="Service *"
                    onChange={handleChange('serviceId')}
                  >
                    <MenuItem value={0}>
                      <em>Select a service</em>
                    </MenuItem>
                    {services.map(service => (
                      <MenuItem key={service.id} value={service.id}>
                        {service.title} - ${service.price}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.serviceId && (
                    <Typography variant="caption" color="error">
                      {errors.serviceId}
                    </Typography>
                  )}
                </FormControl>

                <FormControl fullWidth margin="normal">
                  <InputLabel>Diagnostic Center</InputLabel>
                  <Select
                    value={formData.diagnosticCenterId}
                    label="Diagnostic Center"
                    onChange={handleChange('diagnosticCenterId')}
                  >
                    <MenuItem value="">
                      <em>Select a diagnostic center</em>
                    </MenuItem>
                    {diagnosticCenters.map(center => (
                      <MenuItem key={center.id} value={center.id}>
                        {center.name} - {center.address} - {center.city}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Display selected service image */}
                {formData.serviceId !== 0 &&
                  (() => {
                    const selectedService = services.find(
                      s => s.id === formData.serviceId
                    );
                    const imageUrl =
                      selectedService?.image_main || selectedService?.image;

                    if (imageUrl) {
                      return (
                        <Box sx={{ mt: 2 }}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            gutterBottom
                          >
                            Service Image
                          </Typography>
                          <Box
                            component="img"
                            src={imageUrl}
                            alt={selectedService?.title || 'Service'}
                            sx={{
                              width: '100%',
                              height: 'auto',
                              maxHeight: 200,
                              objectFit: 'cover',
                              borderRadius: 1,
                              border: '1px solid',
                              borderColor: 'divider',
                            }}
                          />
                        </Box>
                      );
                    }
                    return null;
                  })()}

                <TextField
                  fullWidth
                  label="Amount"
                  type="number"
                  value={formData.amount}
                  onChange={handleChange('amount')}
                  error={!!errors.amount}
                  helperText={errors.amount}
                  margin="normal"
                  required
                  InputProps={{
                    startAdornment: (
                      <Typography sx={{ mr: 1 }}>{DEFAULT_CURRENCY}</Typography>
                    ),
                  }}
                />

                <Divider sx={{ my: 2 }} />
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Created on
                </Typography>
                <Typography variant="h6">
                  {formatDate(formData.createdAt)}
                </Typography>
              </CardContent>
            </Card>

            {/* Service Information Card */}
            {formData.serviceId !== 0 && (
              <Card sx={{ mt: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Service Information
                  </Typography>
                  <Divider sx={{ mb: 2 }} />

                  {(() => {
                    const selectedService = services.find(
                      s => s.id === formData.serviceId
                    );
                    if (selectedService) {
                      return (
                        <>
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                              Service Title
                            </Typography>
                            <Typography variant="body1" fontWeight="bold">
                              {selectedService.title}
                            </Typography>
                          </Box>

                          {selectedService.description && (
                            <Box sx={{ mb: 2 }}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Description
                              </Typography>
                              <Typography variant="body2">
                                {selectedService.description}
                              </Typography>
                            </Box>
                          )}

                          {selectedService.duration && (
                            <Box sx={{ mb: 2 }}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Duration
                              </Typography>
                              <Typography variant="body1">
                                {selectedService.duration} minutes
                              </Typography>
                            </Box>
                          )}

                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              Price
                            </Typography>
                            <Typography variant="h6" color="primary">
                              {DEFAULT_CURRENCY}
                              {selectedService.price}
                            </Typography>
                          </Box>
                        </>
                      );
                    }
                    return null;
                  })()}
                </CardContent>
              </Card>
            )}

            {/* Diagnostic Center Card */}
            <Card sx={{ mt: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Diagnostic Center
                </Typography>
                <Divider sx={{ mb: 2 }} />

                {formData.diagnosticCenterAddress && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Address
                    </Typography>
                    <Typography variant="body1">
                      {formData.diagnosticCenterAddress}
                    </Typography>
                  </Box>
                )}

                {formData.diagnosticCenterCity && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      City
                    </Typography>
                    <Typography variant="body1">
                      {formData.diagnosticCenterCity}
                    </Typography>
                  </Box>
                )}

                {formData.diagnosticCenterPostalCode && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Postal Code
                    </Typography>
                    <Typography variant="body1">
                      {formData.diagnosticCenterPostalCode}
                    </Typography>
                  </Box>
                )}

                {formData.diagnosticCenterEmail && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Email
                    </Typography>
                    <Typography variant="body1">
                      {formData.diagnosticCenterEmail}
                    </Typography>
                  </Box>
                )}

                {formData.diagnosticCenterPhone && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Phone
                    </Typography>
                    <Typography variant="body1">
                      {formData.diagnosticCenterPhone}
                    </Typography>
                  </Box>
                )}

                {!formData.diagnosticCenterId &&
                  !formData.diagnosticCenterAddress &&
                  !formData.diagnosticCenterCity &&
                  !formData.diagnosticCenterPostalCode &&
                  !formData.diagnosticCenterEmail &&
                  !formData.diagnosticCenterPhone && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontStyle: 'italic' }}
                    >
                      No diagnostic center information available
                    </Typography>
                  )}
              </CardContent>
            </Card>

            {/* Danger Zone */}
            {/* <Card
              sx={{ mt: 2, border: '1px solid', borderColor: 'error.light' }}
            >
              <CardContent>
                <Typography variant="h6" color="error" gutterBottom>
                  Danger Zone
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Once you delete a booking, there is no going back. Please be
                  certain.
                </Typography>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<Delete />}
                  onClick={() => setDeleteDialog(true)}
                  fullWidth
                >
                  Delete Booking
                </Button>
              </CardContent>
            </Card> */}
          </Grid>

          {/* Form Section */}
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Box component="form" onSubmit={handleSubmit}>
                  <Grid container spacing={3}>
                    {/* Patient Information */}
                    <Grid item xs={12}>
                      <Typography variant="h6" gutterBottom>
                        Patient Information
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="First Name"
                        value={formData.firstName}
                        onChange={handleChange('firstName')}
                        error={!!errors.firstName}
                        helperText={errors.firstName}
                        required
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Last Name"
                        value={formData.lastName}
                        onChange={handleChange('lastName')}
                        error={!!errors.lastName}
                        helperText={errors.lastName}
                        required
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange('email')}
                        error={!!errors.email}
                        helperText={errors.email}
                        required
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Phone"
                        value={formData.phone}
                        onChange={handleChange('phone')}
                        error={!!errors.phone}
                        helperText={errors.phone || 'Format: +542121516'}
                        placeholder="+542121516"
                        required
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Date of Birth"
                        type="date"
                        value={formData.dob}
                        onChange={handleChange('dob')}
                        error={!!errors.dob}
                        helperText={errors.dob}
                        required
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel>Gender</InputLabel>
                        <Select
                          value={formData.gender}
                          label="Gender"
                          onChange={handleChange('gender')}
                        >
                          {genderOptions.map(option => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Health Card"
                        value={formData.healthCard}
                        onChange={handleChange('healthCard')}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Version Code"
                        value={formData.versionCode}
                        onChange={handleChange('versionCode')}
                      />
                    </Grid>

                    {/* Appointment Details */}
                    <Grid item xs={12}>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="h6" gutterBottom>
                        Appointment Details
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Booking Date"
                        type="date"
                        value={formData.bookingDate}
                        onChange={handleChange('bookingDate')}
                        error={!!errors.bookingDate}
                        helperText={errors.bookingDate}
                        required
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Booking Time"
                        type="time"
                        value={formData.bookingTime}
                        onChange={handleChange('bookingTime')}
                        error={!!errors.bookingTime}
                        helperText={errors.bookingTime}
                        required
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Medical Information"
                        multiline
                        rows={3}
                        value={formData.medicalInformation}
                        onChange={handleChange('medicalInformation')}
                      />
                    </Grid>

                    {/* Address Information */}
                    <Grid item xs={12}>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="h6" gutterBottom>
                        Address Information
                      </Typography>
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Street Address"
                        value={formData.streetAddress}
                        onChange={handleChange('streetAddress')}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="City"
                        value={formData.city}
                        onChange={handleChange('city')}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Postal Code"
                        value={formData.postalCode}
                        onChange={handleChange('postalCode')}
                      />
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 3 }} />

                  <Box display="flex" justifyContent="flex-end" gap={2}>
                    <Button
                      variant="outlined"
                      onClick={handleCancel}
                      startIcon={<Cancel />}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      startIcon={
                        saving ? <CircularProgress size={20} /> : <Save />
                      }
                      disabled={saving}
                      sx={{
                        background:
                          'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
                        '&:hover': {
                          background:
                            'linear-gradient(45deg, #1565c0 30%, #1976d2 90%)',
                        },
                      }}
                    >
                      {saving ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
        <DialogTitle>Delete Booking</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete booking "{formData.bookingRef}"?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default EditBooking;
